import type { FC, ReactNode } from "react";
import { useId } from "react";
import { ArrowDown, ArrowDownRight, ArrowUp, ArrowUpRight, Copy01, Eye, Share01, TrendDown01, TrendUp01, Zap } from "@untitledui/icons";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";
import type { Props as DotProps } from "recharts/types/shape/Dot";
import { Dropdown } from "../../base/dropdown/dropdown";
import { FeaturedIcon } from "../../foundations/featured-icon/featured-icon";
import { cx } from "../../../utils/cx";

const ActionsDropdown = () => (
    <Dropdown.Root>
        <Dropdown.DotsButton />

        <Dropdown.Popover className="w-min">
            <Dropdown.Menu>
                <Dropdown.Item icon={Eye}>
                    <span className="pe-4">جزئیات</span>
                </Dropdown.Item>
                <Dropdown.Item icon={Share01}>
                    <span className="pe-4">اشتراک‌گذاری</span>
                </Dropdown.Item>
                <Dropdown.Item icon={Copy01}>
                    <span className="pe-4">کپی لینک</span>
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown.Popover>
    </Dropdown.Root>
);

interface MetricChangeIndicatorProps {
    type: "simple" | "trend" | "modern";
    trend: "positive" | "negative";
    value?: ReactNode;
    className?: string;
}

export const MetricChangeIndicator = ({ type, trend, value, className }: MetricChangeIndicatorProps) => {
    const icons = {
        positive: {
            simple: ArrowUp,
            trend: TrendUp01,
            modern: ArrowUpRight,
        },
        negative: {
            simple: ArrowDown,
            trend: TrendDown01,
            modern: ArrowDownRight,
        },
    };

    const Icon = icons[trend][type];

    return (
        <div
            className={cx(
                "flex items-center",
                type === "simple" ? "gap-0.5" : "gap-1",
                type === "modern" && "rounded-md bg-primary py-0.5 pe-2 ps-1.5 shadow-xs ring-1 ring-primary ring-inset",
                className,
            )}
        >
            <Icon
                className={cx(
                    "stroke-[3px] text-fg-success-secondary",
                    type === "modern" ? "size-3" : "size-4",
                    trend === "negative" ? "text-fg-error-secondary" : "text-fg-success-secondary",
                )}
            />
            <span
                className={cx(
                    "text-sm font-medium text-secondary",
                    type === "modern" ? "text-secondary" : trend === "negative" ? "text-error-primary" : "text-success-primary",
                )}
            >
                {value}
            </span>
        </div>
    );
};

export const MetricsSimple = ({
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    type,
    trend,
    change,
    footer,
    className,
}: {
    title?: ReactNode;
    subtitle?: ReactNode;
    type: "simple" | "trend" | "modern";
    trend: "positive" | "negative";
    change?: ReactNode;
    footer?: ReactNode;
    className?: string;
}) => {
    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-2 px-4 py-5 md:px-5">
                <h3 className="text-sm font-medium text-tertiary">{subtitle}</h3>

                <div className="flex items-end gap-4">
                    <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                    <MetricChangeIndicator type={type} trend={trend} value={change} />
                </div>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-4 md:px-5">{footer}</div>}
        </div>
    );
};

export const MetricsIcon01 = ({
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    footer,
    className,
}: {
    title?: ReactNode;
    subtitle?: ReactNode;
    footer?: ReactNode;
    className?: string;
}) => {
    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:gap-5 md:px-5">
                <FeaturedIcon color="success" theme="light" icon={TrendUp01} size="lg" />

                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-medium text-tertiary">{subtitle}</h3>

                    <div className="flex items-end gap-4">
                        <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                        <MetricChangeIndicator type="modern" trend="positive" value="100%" />
                    </div>
                </div>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-4 md:px-5">{footer}</div>}
        </div>
    );
};

export const MetricsIcon02 = ({
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    footer,
    className,
}: {
    title?: ReactNode;
    subtitle?: ReactNode;
    footer?: ReactNode;
    className?: string;
}) => {
    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:gap-5 md:px-5">
                <div className="flex items-center gap-3">
                    <FeaturedIcon color="brand" theme="light" size="lg" icon={Zap} />
                    <h3 className="text-md font-semibold text-primary">{subtitle}</h3>
                </div>

                <div className="flex flex-col gap-3">
                    <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                    <div className="flex gap-2">
                        <MetricChangeIndicator type="simple" trend="positive" value="100%" />
                        <span className="text-sm font-medium text-tertiary">نسبت به ماه قبل</span>
                    </div>
                </div>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-4 md:px-5">{footer}</div>}
        </div>
    );
};

export const MetricsIcon03 = ({
    icon = TrendUp01,
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    change = "100%",
    changeTrend = "positive",
    actions = true,
    className,
    footer,
}: {
    icon?: FC<{ className?: string }>;
    title?: ReactNode;
    subtitle?: ReactNode;
    change?: ReactNode;
    changeTrend?: "positive" | "negative";
    actions?: boolean;
    footer?: ReactNode;
    className?: string;
}) => {
    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:gap-5 md:px-5">
                <FeaturedIcon color="gray" theme="modern" icon={icon || TrendUp01} size="lg" />

                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold text-tertiary">{subtitle}</h3>

                    <div className="flex flex-wrap items-center gap-3 lg:gap-4">
                        <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                        <div className="flex gap-2">
                            <MetricChangeIndicator type="trend" trend={changeTrend} value={change} />
                            <span className="text-sm font-medium text-tertiary">نسبت به ماه قبل</span>
                        </div>
                    </div>
                </div>

                {actions && (
                    <div className="absolute top-4 end-4 md:top-5 md:end-5">
                        <ActionsDropdown />
                    </div>
                )}
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-3 pe-4 md:p-4 md:pe-5">{footer}</div>}
        </div>
    );
};

export const MetricsIcon04 = ({
    icon = TrendUp01,
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    change = "100%",
    changeTrend = "positive",
    actions = true,
    footer,
    className,
}: {
    icon?: FC<{ className?: string }>;
    title?: ReactNode;
    subtitle?: ReactNode;
    change?: ReactNode;
    changeTrend?: "positive" | "negative";
    actions?: boolean;
    footer?: ReactNode;
    className?: string;
}) => {
    return (
        <div className={cx("min-w-70 rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:flex-row md:px-5">
                <FeaturedIcon color="gray" theme="modern" icon={icon} size="md" />

                <div className="flex w-full flex-col gap-2">
                    <h3 className="text-sm font-semibold text-tertiary">{subtitle}</h3>

                    <div className="flex w-full flex-wrap items-center justify-between gap-4">
                        <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>

                        <MetricChangeIndicator type="modern" trend={changeTrend} value={change} />
                    </div>
                </div>

                {actions && (
                    <div className="absolute top-4 end-4 md:top-5 md:end-5">
                        <ActionsDropdown />
                    </div>
                )}
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-3 pe-4 md:p-4 md:pe-5">{footer}</div>}
        </div>
    );
};

const CustomizedDot = (props: DotProps & { payload?: { highlight?: boolean }; dotColor?: string }) => {
    if (!props.payload?.highlight) {
        return null;
    }

    const size = 20;

    return (
        <svg
            x={Number(props.cx) - size / 2}
            y={Number(props.cy) - size / 2}
            width={size}
            height={size}
            viewBox="0 0 20 20"
            fill="none"
            className={cx("text-fg-success-secondary", props.dotColor)}
        >
            <rect x="1.75" y="1.625" width="17.25" height="17.25" rx="8.625" className="stroke-current" strokeOpacity={0.2} strokeWidth="2" />
            <rect x="6.125" y="6" width="8.5" height="8.5" rx="4.25" className="fill-bg-primary stroke-current" strokeWidth="2" />
        </svg>
    );
};

const lineData = [
    { value: 10 },
    { value: 15 },
    { value: 12 },
    { value: 20 },
    { value: 18 },
    { value: 25 },
    { value: 30 },
    { value: 28 },
    { value: 32 },
    { value: 35 },
    { value: 40, highlight: true },
    { value: 32 },
    { value: 40 },
    { value: 50 },
    { value: 55 },
];

export const MetricsChart01 = ({
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    change = "100%",
    changeDescription = "نسبت به ماه قبل",
    actions = true,
    chartData = lineData,
    trend = "positive",
    footer,
    className,
}: {
    title?: ReactNode;
    subtitle?: ReactNode;
    /** null hides the comparison row (nothing to compare yet). */
    change?: ReactNode | null;
    changeDescription?: ReactNode;
    actions?: boolean;
    trend?: "positive" | "negative";
    chartData?: { value: number; highlight?: boolean }[];
    footer?: ReactNode;
    className?: string;
}) => {
    const id = useId();

    const chartColor = trend === "positive" ? "text-fg-success-secondary" : "text-fg-error-secondary";

    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-5 px-4 py-5 md:px-5">
                <h3 className="text-md font-semibold text-primary">{subtitle}</h3>

                <div className="flex items-end justify-between gap-4">
                    <div className="flex flex-col gap-3">
                        <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                        {change === null ? (
                            <span className="text-sm font-medium text-quaternary">هنوز داده‌ای برای مقایسه نیست</span>
                        ) : (
                            <div className="flex gap-2">
                                <MetricChangeIndicator type="simple" trend={trend} value={change} />
                                <span className="text-sm font-medium text-tertiary">{changeDescription}</span>
                            </div>
                        )}
                    </div>

                    <AreaChart
                        height={56}
                        width={112}
                        data={chartData}
                        margin={{
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                        }}
                    >
                        <defs>
                            <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="currentColor" className={chartColor} stopOpacity="1" />
                                <stop offset="95%" stopColor="currentColor" className={chartColor} stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        <Area
                            isAnimationActive={false}
                            className={chartColor}
                            dataKey="value"
                            type="monotone"
                            stroke="currentColor"
                            strokeWidth={2}
                            fill={`url(#gradient-${id})`}
                            fillOpacity={0.2}
                            dot={<CustomizedDot dotColor={chartColor} />}
                            activeDot={false}
                        />
                    </AreaChart>
                </div>

                {actions && (
                    <div className="absolute top-4 end-4 md:top-5 md:end-5">
                        <ActionsDropdown />
                    </div>
                )}
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-4 md:px-5">{footer}</div>}
        </div>
    );
};

const lineData2 = [
    { a: 10, b: 0 },
    { a: 5, b: 30 },
    { a: 30, b: 20 },
    { a: 20, b: 35 },
];

export const MetricsChart02 = ({
    icon = Eye,
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    change = "100%",
    changeTrend = "positive",
    footer,
    className,
}: {
    icon?: FC<{ className?: string }>;
    title?: ReactNode;
    subtitle?: ReactNode;
    change?: ReactNode;
    changeTrend?: "positive" | "negative";
    footer?: ReactNode;
    className?: string;
}) => {
    const id = useId();
    const chartColor = changeTrend === "positive" ? "text-fg-success-secondary" : "text-fg-error-secondary";

    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:gap-5 md:px-5">
                <div className="flex items-center gap-3">
                    <FeaturedIcon color="gray" theme="modern" size="lg" icon={icon} />
                    <h3 className="text-md font-semibold text-primary">{subtitle}</h3>
                </div>

                <div className="flex items-end justify-between gap-4">
                    <div className="flex items-start gap-2">
                        <p className="flex-1 text-display-sm font-semibold text-primary lg:text-display-md">{title}</p>
                        <MetricChangeIndicator type="trend" trend={changeTrend} value={change} />
                    </div>

                    <AreaChart
                        height={56}
                        width={128}
                        data={lineData2}
                        margin={{
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                        }}
                    >
                        <defs>
                            <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="currentColor" className={chartColor} stopOpacity="1" />
                                <stop offset="95%" stopColor="currentColor" className={chartColor} stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        <Area
                            className={chartColor}
                            dataKey="a"
                            type="monotone"
                            strokeWidth={0}
                            fill="currentColor"
                            fillOpacity={0.2}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            dataKey="b"
                            type="monotone"
                            strokeWidth={0}
                            fill={`url(#gradient-${id})`}
                            fillOpacity={0.2}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                    </AreaChart>
                </div>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-3 pe-4 md:p-4 md:pe-5">{footer}</div>}
        </div>
    );
};

const lineData3 = [{ value: 0 }, { value: 9 }, { value: 6 }, { value: 15 }];

export const MetricsChart03 = ({
    title = "2,000",
    subtitle = "۲۴ ساعت گذشته",
    type = "trend",
    change,
    changeTrend,
    changeDescription,
    chartColor,
    chartAreaFill,
    chartCurveType,
    chartData = lineData3,
    footer,
    className,
}: {
    title?: ReactNode;
    subtitle?: ReactNode;
    type?: "simple" | "trend" | "modern";
    change: string;
    changeTrend: "positive" | "negative";
    changeDescription?: ReactNode;
    chartColor?: string;
    chartAreaFill?: string;
    chartCurveType?: CurveType;
    chartData?: { value: number }[];
    footer?: ReactNode;
    className?: string;
}) => {
    const id = useId();

    chartColor = chartColor ?? (changeTrend === "positive" ? "text-fg-success-secondary" : "text-fg-error-secondary");

    return (
        <div className={cx("rounded-xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="relative flex flex-col gap-4 px-4 py-5 md:gap-5 md:px-5">
                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-medium text-tertiary">{subtitle}</h3>

                    <div className="flex items-center gap-4">
                        <p className="flex-1 text-display-sm font-semibold text-primary">{title}</p>
                        <div className="flex gap-2">
                            <MetricChangeIndicator type={type} trend={changeTrend} value={change} />
                            {changeDescription && <span className="text-sm font-medium text-tertiary">{changeDescription}</span>}
                        </div>
                    </div>
                </div>

                <ResponsiveContainer initialDimension={{ width: 1, height: 1 }} height={72}>
                    <AreaChart
                        data={chartData}
                        margin={{
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                        }}
                    >
                        <defs>
                            <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="currentColor" className={chartColor} stopOpacity="1" />
                                <stop offset="95%" stopColor="currentColor" className={chartColor} stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        <Area
                            isAnimationActive={false}
                            className={chartColor}
                            dataKey="value"
                            type={chartCurveType || "monotone"}
                            stroke="currentColor"
                            strokeWidth={2}
                            fill={chartAreaFill || `url(#gradient-${id})`}
                            fillOpacity={0.2}
                            activeDot={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>

            {footer && <div className="flex items-center justify-end border-t border-secondary p-3 pe-4 md:p-4 md:pe-5">{footer}</div>}
        </div>
    );
};

export const MetricsChart04 = ({
    title,
    subtitle,
    type = "trend",
    change,
    changeTrend,
    changeDescription,
    chartColor = "text-fg-success-secondary",
    chartAreaFill,
    chartCurveType,
    chartData = lineData3,
    footer,
    className,
}: {
    title: string;
    subtitle: string;
    type?: "simple" | "trend" | "modern";
    change: string;
    changeTrend: "positive" | "negative";
    changeDescription?: ReactNode;
    chartColor?: string;
    chartAreaFill?: string;
    chartCurveType?: CurveType;
    chartData?: { value: number }[];
    footer?: ReactNode;
    className?: string;
}) => {
    const id = useId();

    chartColor = chartColor ?? (changeTrend === "positive" ? "text-fg-success-secondary" : "text-fg-error-secondary");

    return (
        <div className={cx("flex flex-col overflow-hidden rounded-xl bg-secondary shadow-xs ring-1 ring-secondary ring-inset", className)}>
            <div className="mb-0.5 px-4 pt-3 pb-2 md:px-5">
                <h3 className="text-sm font-semibold text-primary">{subtitle}</h3>
            </div>
            <div className="relative flex flex-col gap-4 rounded-xl bg-primary px-4 py-5 shadow-xs ring-1 ring-secondary ring-inset md:gap-5 md:px-5">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                        <p className="text-display-sm font-semibold text-primary">{title}</p>
                        <div className="flex gap-2">
                            <MetricChangeIndicator type={type} trend={changeTrend} value={change} />
                            {changeDescription && <span className="text-sm font-medium text-tertiary">{changeDescription}</span>}
                        </div>
                    </div>
                </div>

                <ResponsiveContainer initialDimension={{ width: 1, height: 1 }} height={56}>
                    <AreaChart
                        data={chartData}
                        margin={{
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                        }}
                        className={chartColor}
                    >
                        <defs>
                            <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="currentColor" className={chartColor} stopOpacity="1" />
                                <stop offset="95%" stopColor="currentColor" className={chartColor} stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        <Area
                            isAnimationActive={false}
                            className={chartColor}
                            dataKey="value"
                            type={chartCurveType || "monotone"}
                            stroke="currentColor"
                            strokeWidth={2}
                            fill={chartAreaFill || `url(#gradient-${id})`}
                            fillOpacity={0.2}
                            activeDot={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>

                <div className="absolute top-4 end-4 md:top-5 md:end-5">
                    <ActionsDropdown />
                </div>
            </div>
            {footer && <div className="flex items-center justify-end py-3 pe-4 ps-3 md:pe-5 md:ps-4">{footer}</div>}
        </div>
    );
};
